import { Row } from "@tanstack/react-table";
import { Fragment, type JSX, type ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/ContextMenu";
import { Any } from "@/interfaces/globals";
import { cn } from "@/utils/core/generalUtils";

interface WpxRightClickActionButtonProps {
  children: ReactNode;
  row?: Row<Any>;
  data?: (row: Row<Any> | undefined) => ActionButtonDataTypes[] | undefined;
  enableEnhancedUX?: boolean;
}

const WpxRightClickActionButton = ({
  children,
  row,
  data,
  enableEnhancedUX,
}: WpxRightClickActionButtonProps): JSX.Element => {
  const [isContextOpen, setContextState] = useState(false);

  const visibleActions = useMemo(() => {
    const actions = data?.(row) ?? [];

    return actions.filter((item) => !item.hide);
  }, [data, row]);

  if (!visibleActions.length) {
    return <>{children}</>;
  }

  return (
    <ContextMenu modal={isContextOpen} onOpenChange={setContextState}>
      <ContextMenuTrigger
        asChild
        {...(enableEnhancedUX && isContextOpen && { "data-open": "true" })}
      >
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent>
        {visibleActions.map((item) => {
          const Icon = item?.icon;

          return item.isLink ? (
            <Link to={item.isLink} state={{ id: item?.id }} key={item.name}>
              <ContextMenuItem
                className={cn(
                  `flex items-center  !pr-5 font-medium gap-2`,
                  item.isTrash && "text-red-600 hover:!text-red-600",
                )}
              >
                {Icon && <Icon size={16} />}
                <span>{item.name}</span>
              </ContextMenuItem>
            </Link>
          ) : (
            <Fragment key={item.name}>
              <ContextMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.onClick) {
                    item.onClick();
                  }
                }}
                className={cn(
                  `flex items-center  !pr-5 font-medium gap-2`,
                  item.isTrash && "text-red-600 hover:!text-red-600",
                  item?.textClassName,
                )}
              >
                {Icon && <Icon size={16} />}
                <span>{item.name}</span>
              </ContextMenuItem>
            </Fragment>
          );
        })}
      </ContextMenuContent>
    </ContextMenu>
  );
};

export default WpxRightClickActionButton;
